// Adapted from: https://codepen.io/Megalovania/pen/QWoRqqE

const { floor, random, sqrt } = Math

function getCircuitColor() {
  const style = getComputedStyle(document.documentElement)
  return style.getPropertyValue('--circuit-line').trim() || "#f4fcf2"
}

// Directions defined clockwise for cell direction assignment
const dirs = [[0, 1], [1, 1], [1, 0], [1, -1], [0, -1], [-1, -1], [-1, 0], [-1, 1]]

const MAX_WIRES = 150

export function initCircuitPattern() {
  const circuitColor = getCircuitColor()
  const settings = {
    size: 20,
    wireMaxLen: 40,
    stroke: circuitColor,
    fill: circuitColor,
    blank: circuitColor,
    straightness: 2,
  }
  const circuitPatternBg = document.querySelector(".circuit-pattern-bg")
  const svgCircuitPatternBg = document.querySelector(".circuit-pattern-svg")

  if (!circuitPatternBg || !svgCircuitPatternBg) return

  const { width, height } = circuitPatternBg.getBoundingClientRect()
  svgCircuitPatternBg.setAttribute("width", `${width}`)
  svgCircuitPatternBg.setAttribute("height", `${height}`)

  const rows = floor(height / settings.size)
  const cols = floor(width / settings.size)
  let availableNum = floor(rows * cols)
  const cells = []
  const cellsMap = {}
  const wires = []

  class Cell {
    constructor(x, y) {
      this.x = x
      this.y = y
      this.available = true
      this.dirInd = floor(random() * dirs.length)
    }
  }

  class Wire {
    constructor(start) {
      this.cells = []
      start.available = false
      this.cells.push(start)
      availableNum -= 1
    }

    validNoCrossOver(c1, dirInd) {
      // Horizontal/vertical won't cross
      if ([0, 2, 4, 6].includes(dirInd))
        return true
      // Diagonal directions need crossover checking
      if (dirInd === 1) {
        const c3 = cellsMap[`${c1.x},${c1.y - 1}`]?.available ?? true
        const c4 = cellsMap[`${c1.x + 1},${c1.y}`]?.available ?? true
        if (c3 && c4) return true
      }
      if (dirInd === 3) {
        const c3 = cellsMap[`${c1.x + 1},${c1.y}`]?.available ?? true
        const c4 = cellsMap[`${c1.x},${c1.y + 1}`]?.available ?? true
        if (c3 && c4) return true
      }
      if (dirInd === 5) {
        const c3 = cellsMap[`${c1.x - 1},${c1.y}`]?.available ?? true
        const c4 = cellsMap[`${c1.x},${c1.y + 1}`]?.available ?? true
        if (c3 && c4) return true
      }
      if (dirInd === 7) {
        const c3 = cellsMap[`${c1.x - 1},${c1.y}`]?.available ?? true
        const c4 = cellsMap[`${c1.x},${c1.y - 1}`]?.available ?? true
        if (c3 && c4) return true
      }
      return false
    }

    generate() {
      while (this.cells.length < settings.wireMaxLen) {
        const last = this.cells[this.cells.length - 1]
        const tries = random() < 0.5 ? [0, 1, -1] : [0, -1, 1]

        while (tries.length > 0) {
          let dirInd = last.dirInd + tries.splice(floor(random() ** settings.straightness * tries.length), 1)[0]
          dirInd = dirInd < 0 ? 8 + dirInd : dirInd % 8
          const dir = dirs[dirInd]

          const x = last.x + dir[0]
          const y = last.y + dir[1]
          const index = y * cols + x
          const next = index >= 0 && index < cells.length ? cells[index] : false

          if (x < 0 || x >= cols || y < 0 || y >= rows || !next || !next.available || !this.validNoCrossOver(last, dirInd))
            continue

          next.available = false
          next.dirInd = dirInd
          availableNum -= 1
          this.cells.push(next)
          break
        }
        if (tries.length === 0) break
      }
    }

    draw(fragment) {
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
      const circle1 = document.createElementNS("http://www.w3.org/2000/svg", "circle")
      const circle2 = document.createElementNS("http://www.w3.org/2000/svg", "circle")

      let d = ""
      const s = settings.size
      const r = random() * (s / 6) + s / 6

      circle1.setAttribute("r", `${r}`)
      circle2.setAttribute("r", `${r}`)
      circle1.setAttribute("stroke", circuitColor)
      circle2.setAttribute("stroke", circuitColor)
      const isFill = random() > 0.5
      circle1.setAttribute("fill", isFill ? settings.fill : settings.blank)
      circle2.setAttribute("fill", isFill ? settings.fill : settings.blank)

      // Calculate path length manually to avoid getTotalLength() layout thrashing
      let length = 0

      for (let i = 0; i < this.cells.length; i += 1) {
        const cur = this.cells[i]
        if (i === 0) {
          d += `M ${cur.x * s + s / 2} ${cur.y * s + s / 2}`
          circle1.setAttribute("cx", `${cur.x * s + s / 2}`)
          circle1.setAttribute("cy", `${cur.y * s + s / 2}`)
        }

        if (i < this.cells.length)
          d += ` L ${cur.x * s + s / 2} ${cur.y * s + s / 2}`

        if (i > 0) {
          const prev = this.cells[i - 1]
          const dx = (cur.x - prev.x) * s
          const dy = (cur.y - prev.y) * s
          length += sqrt(dx * dx + dy * dy)
        }

        if (i === this.cells.length - 1) {
          circle2.setAttribute("cx", `${cur.x * s + s / 2}`)
          circle2.setAttribute("cy", `${cur.y * s + s / 2}`)
        }
      }

      path.setAttribute("d", d)
      path.setAttribute("fill", "none")
      path.setAttribute("stroke", settings.stroke)
      path.setAttribute("stroke-width", `${r * 2}`)
      const isRepeat = random() > 0.8
      path.style.cssText = isRepeat
        ? `--len: ${length}; --len-1:${-length}; --len-3:${-3 * length}`
        : `--len: ${length}; --len-1:${-length}`
      path.classList.add(isRepeat ? "animated-path-repeat" : "animated-path-once")
      fragment.append(path)
      fragment.append(circle1)
      fragment.append(circle2)
    }
  }

  // Initialize cells grid
  for (let y = 0; y < rows; y += 1) {
    for (let x = 0; x < cols; x += 1) {
      const cell = new Cell(x, y)
      cells.push(cell)
      cellsMap[`${x},${y}`] = cell
    }
  }

  // Generate wires into a DocumentFragment for single DOM insertion
  const fragment = document.createDocumentFragment()

  while (wires.length < MAX_WIRES && wires.length < availableNum) {
    const cell = cells[floor(random() * cells.length)]
    if (!cell.available) continue

    const wire = new Wire(cell)
    wires.push(wire)
    wire.generate()
    wire.draw(fragment)
  }

  svgCircuitPatternBg.appendChild(fragment)
}

// Clear existing pattern before regenerating
export function clearCircuitPattern() {
  const svg = document.querySelector(".circuit-pattern-svg")
  if (svg) svg.innerHTML = ""
}

// Debounced resize handler
let resizeTimeout
function handleResize() {
  clearTimeout(resizeTimeout)
  resizeTimeout = setTimeout(() => {
    clearCircuitPattern()
    initCircuitPattern()
  }, 150)
}

// Run on page load and Turbo navigation
document.addEventListener("DOMContentLoaded", initCircuitPattern)
document.addEventListener("turbo:load", initCircuitPattern)
window.addEventListener("resize", handleResize)
